import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { classNames } from 'primereact/utils';
import axiosInstance from '../config/axios.config';
import { parseApiDate, formatDateTime } from '../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatINR = (amount) => `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Description shared by table + mobile.
const rowDescription = (t) => {
    if (t.kind === 'payment') {
        return `${t.label || 'Payment'}${t.plan_name ? ` — ${t.plan_name}${t.billing_interval ? ` (${t.billing_interval})` : ''}` : ''}`;
    }
    return t.reason || '—';
};

// Mobile card header — works for both money payments and credit movements.
const HistoryMobileRow = ({ txn }) => {
    const isPayment = txn.kind === 'payment';
    const tagValue = isPayment
        ? formatINR(txn.amount)
        : (txn.type === 'credit' ? `+${txn.credits} cr` : `-${txn.credits} cr`);
    const tagSeverity = isPayment ? 'info' : (txn.type === 'credit' ? 'success' : 'danger');
    return (
        <div className="flex justify-content-between align-items-center gap-2 w-full">
            <div className="flex align-items-center gap-2 flex-1 min-w-0">
                <div className="flex flex-column min-w-0">
                    <span className="font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {rowDescription(txn)}
                    </span>
                    <span className="text-xs text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {formatDateTime(txn.created_at)}
                    </span>
                </div>
            </div>
            <Tag value={tagValue} severity={tagSeverity} className="text-xs flex-shrink-0" />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const TransactionHistory = () => {
    const toast = useRef(null);

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [kindFilter, setKindFilter] = useState(null); // null=all, 'payment', 'credit', 'debit'
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [mobileIndex, setMobileIndex] = useState(null);

    const kindOptions = useMemo(() => ([
        { label: 'All activity', value: null },
        { label: 'Payments (₹)', value: 'payment' },
        { label: 'Credits added', value: 'credit' },
        { label: 'Credits used', value: 'debit' },
    ]), []);

    // Unified load: MONEY (subscription + credit-pack payments) + CREDITS
    // (recharge, plan grants, report spends), merged + sorted, paginated in JS.
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const params = { per_page: 500, page: 1 };
                if (dateFrom) params.date_from = dateFrom.toISOString().split('T')[0];
                if (dateTo) params.date_to = dateTo.toISOString().split('T')[0];

                // Credit "type" filter (1=credit, 2=debit) only applies to movements.
                const creditParams = { ...params };
                if (kindFilter === 'credit') creditParams.type = 1;
                if (kindFilter === 'debit') creditParams.type = 2;

                const wantCredits = kindFilter !== 'payment';
                const wantPayments = kindFilter === null || kindFilter === 'payment';

                const [creditsRes, paymentsRes] = await Promise.allSettled([
                    wantCredits
                        ? axiosInstance.get('/credit-recharge/transactions', { params: creditParams })
                        : Promise.resolve({ data: { success: true, data: [] } }),
                    wantPayments
                        ? axiosInstance.get('/credit-recharge/payments', { params })
                        : Promise.resolve({ data: { success: true, data: [] } }),
                ]);
                if (cancelled) return;

                const creditRows = (creditsRes.status === 'fulfilled' && creditsRes.value.data?.success)
                    ? (creditsRes.value.data.data || []).map((t) => ({ ...t, kind: 'credit_movement' }))
                    : [];
                const paymentRows = (paymentsRes.status === 'fulfilled' && paymentsRes.value.data?.success)
                    ? (paymentsRes.value.data.data || [])
                    : [];

                const ts = (s) => { const d = parseApiDate(s); return d ? d.getTime() : 0; };
                const merged = [...creditRows, ...paymentRows].sort((a, b) => ts(b.created_at) - ts(a.created_at));

                setTransactions(merged);
                setTotalRecords(merged.length);
                setFirst(0);
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load transaction history:', error?.message || 'Unknown error');
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load transaction history', life: 3000 });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [dateFrom, dateTo, kindFilter, refreshNonce]);

    useEffect(() => { setMobileIndex(null); }, [first, dateFrom, dateTo, kindFilter]);

    const clearFilters = () => { setDateFrom(null); setDateTo(null); setKindFilter(null); setFirst(0); };

    // ── Column templates ──
    const dateBodyTemplate = (t) => <span className="text-sm text-700">{formatDateTime(t.created_at)}</span>;

    const typeBodyTemplate = (t) => {
        if (t.kind === 'payment') return <Tag value="Payment" severity="info" icon="pi pi-indian-rupee" />;
        const isCredit = t.type === 'credit';
        return <Tag value={isCredit ? 'Credit' : 'Debit'} severity={isCredit ? 'success' : 'danger'} icon={`pi ${isCredit ? 'pi-plus' : 'pi-minus'}`} />;
    };

    const amountBodyTemplate = (t) => {
        if (t.kind === 'payment') return <span className="font-semibold text-sm text-900">{formatINR(t.amount)}</span>;
        const isCredit = t.type === 'credit';
        return (
            <span className={classNames('font-semibold text-sm', { 'text-green-600': isCredit, 'text-red-600': !isCredit })}>
                {isCredit ? '+' : '-'}{t.credits} cr
            </span>
        );
    };

    const descBodyTemplate = (t) => <span className="text-sm text-700">{rowDescription(t)}</span>;

    const referenceBodyTemplate = (t) => (
        <span className={`text-sm ${t.reference ? 'text-700' : 'text-500'} white-space-nowrap overflow-hidden text-overflow-ellipsis`} style={{ maxWidth: '220px', display: 'inline-block' }} title={t.reference || ''}>
            {t.reference || '—'}
        </span>
    );

    const pagedRows = useMemo(() => transactions.slice(first, first + rows), [transactions, first, rows]);

    return (
        <div className="grid">
            <Toast ref={toast} />

            <div className="col-12">
                {/* Page header (P6) */}
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                    <div>
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Transaction History</h1>
                        <p className="text-600 text-sm m-0">Your subscription payments, credit purchases, and credit usage</p>
                    </div>
                    <Button
                        icon="pi pi-refresh"
                        label="Refresh"
                        outlined
                        size="small"
                        onClick={() => setRefreshNonce((n) => n + 1)}
                        loading={loading}
                    />
                </div>

                <Card>
                    {/* Filter bar (P7) */}
                    <div className="surface-ground p-3 border-round mb-3">
                        <div className="grid">
                            <div className="col-6 md:col">
                                <Calendar value={dateFrom} onChange={(e) => { setDateFrom(e.value); setFirst(0); }} placeholder="From date" dateFormat="dd/mm/yy" showIcon iconDisplay="input" className="w-full p-inputtext-sm" />
                            </div>
                            <div className="col-6 md:col">
                                <Calendar value={dateTo} onChange={(e) => { setDateTo(e.value); setFirst(0); }} placeholder="To date" dateFormat="dd/mm/yy" showIcon iconDisplay="input" className="w-full p-inputtext-sm" />
                            </div>
                            <div className="col-12 md:col">
                                <Dropdown value={kindFilter} options={kindOptions} onChange={(e) => { setKindFilter(e.value); setFirst(0); }} optionLabel="label" optionValue="value" placeholder="All activity" className="w-full p-inputtext-sm" />
                            </div>
                            <div className="hidden md:block md:col" aria-hidden="true"></div>
                            <div className="col-12 md:col flex gap-2">
                                <Button label="Clear" icon="pi pi-filter-slash" severity="secondary" outlined size="small" className="flex-1 py-1 md:py-2" onClick={clearFilters} disabled={!dateFrom && !dateTo && kindFilter === null} />
                                <Button label="Refresh" icon="pi pi-refresh" size="small" className="flex-1 py-1 md:py-2" onClick={() => setRefreshNonce((n) => n + 1)} loading={loading} />
                            </div>
                        </div>
                    </div>

                    {/* Mobile (< md) — Accordion cards (P17) */}
                    <div className="block md:hidden">
                        {loading ? (
                            <div className="flex flex-column gap-2">
                                <Skeleton height="70px" /><Skeleton height="70px" /><Skeleton height="70px" />
                            </div>
                        ) : pagedRows.length === 0 ? (
                            <div className="flex flex-column align-items-center py-5">
                                <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                <span className="text-600">No transactions found</span>
                            </div>
                        ) : (
                            <>
                                <Accordion activeIndex={mobileIndex} onTabChange={(e) => setMobileIndex(e.index)}>
                                    {pagedRows.map((txn, i) => (
                                        <AccordionTab key={`txn-${txn.kind}-${txn.id || i}`} header={<HistoryMobileRow txn={txn} />}>
                                            <div className="flex flex-column gap-2 text-xs">
                                                <div className="flex align-items-center gap-2">
                                                    <i className="pi pi-calendar text-500 w-1rem" aria-hidden="true" />
                                                    <span className="text-500 font-medium w-6rem">Date</span>
                                                    <span className="text-700">{formatDateTime(txn.created_at)}</span>
                                                </div>
                                                <div className="flex align-items-center gap-2">
                                                    <i className="pi pi-file text-500 w-1rem" aria-hidden="true" />
                                                    <span className="text-500 font-medium w-6rem">Description</span>
                                                    <span className="text-700">{rowDescription(txn)}</span>
                                                </div>
                                                <div className="flex align-items-center gap-2">
                                                    <i className="pi pi-hashtag text-500 w-1rem" aria-hidden="true" />
                                                    <span className="text-500 font-medium w-6rem">Reference</span>
                                                    <span className={txn.reference ? 'text-700' : 'text-500'} style={{ wordBreak: 'break-all' }}>{txn.reference || '—'}</span>
                                                </div>
                                                {txn.note && (
                                                    <div className="flex align-items-center gap-2">
                                                        <i className="pi pi-info-circle text-500 w-1rem" aria-hidden="true" />
                                                        <span className="text-500 font-medium w-6rem">Notes</span>
                                                        <span className="text-600">{txn.note}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </AccordionTab>
                                    ))}
                                </Accordion>
                                <Paginator
                                    first={first}
                                    rows={rows}
                                    totalRecords={totalRecords}
                                    onPageChange={(e) => { setFirst(e.first); setRows(e.rows); }}
                                    template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                    currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                    pageLinkSize={3}
                                    className="text-sm mt-2"
                                />
                            </>
                        )}
                    </div>

                    {/* Desktop (≥ md) — DataTable (P12 frozen Date left) */}
                    <div className="hidden md:block">
                        <DataTable
                            value={pagedRows}
                            loading={loading}
                            scrollable
                            stripedRows
                            showGridlines
                            size="small"
                            className="p-datatable-gridlines"
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                    <span className="text-600">No transactions found</span>
                                </div>
                            }
                        >
                            <Column header="Date" body={dateBodyTemplate} frozen alignFrozen="left" style={{ minWidth: '200px' }} />
                            <Column header="Type" body={typeBodyTemplate} style={{ minWidth: '110px' }} />
                            <Column header="Amount" body={amountBodyTemplate} style={{ minWidth: '110px' }} />
                            <Column header="Description" body={descBodyTemplate} style={{ minWidth: '240px' }} />
                            <Column header="Reference" body={referenceBodyTemplate} style={{ minWidth: '180px' }} />
                            <Column header="Notes" body={(t) => <span className={t.note ? 'text-sm text-600' : 'text-sm text-500'}>{t.note || '—'}</span>} style={{ minWidth: '180px' }} />
                        </DataTable>
                        <Paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPageChange={(e) => { setFirst(e.first); setRows(e.rows); }}
                            rowsPerPageOptions={[10, 25, 50]}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            className="text-sm mt-2"
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default TransactionHistory;
